import { motion } from "framer-motion";
import { Shield, Lock, Key } from "lucide-react";

import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";

// FIN-7 — ConexoesHero
//
// Hero permanente da aba Conexões. Renderiza igual em modo dormente e
// modo ativo (vende a feature mesmo quando OF off). Padrão tipográfico
// baseado nos heros das outras tabs do Financeiro: title display 48–56px,
// segundo line com glow neon verde.
//
// 3 trust badges abaixo da copy: BACEN, AES-256, VOCÊ CONTROLA — não são
// claims de marketing soltos, são fatos: BACEN regula OFB, AES-256 é o
// padrão de criptografia das credenciais no Pluggy, e o user pode
// revogar consent direto pela UI da Pluggy ou pelo nosso DELETE.

const TRUST = [
  { icon: Shield, label: "BACEN",          sub: "Certificado" },
  { icon: Lock,   label: "AES-256",        sub: "Criptografia" },
  { icon: Key,    label: "VOCÊ CONTROLA",  sub: "Revogue qualquer hora" },
];

export default function ConexoesHero() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: [0.2, 0.8, 0.2, 1] }}
    >
      <Card
        variant="elevated"
        className="relative overflow-hidden glass-card-strong gradient-border-green glow-hover p-6 md:p-10"
        data-testid="conexoes-hero"
      >
        {/* Grid texture + radial glow verde */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 grid-texture opacity-50"
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(circle at 8% 60%, rgba(0,255,163,0.18), transparent 55%)," +
              "radial-gradient(circle at 95% 10%, rgba(0,255,163,0.10), transparent 50%)",
          }}
        />

        <div className="relative">
          {/* Pill superior — Open Finance · BACEN */}
          <div
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-brand-green/40 bg-brand-green/10 text-brand-green text-caption font-semibold tracking-wider uppercase"
            data-testid="conexoes-hero-pill"
          >
            <Shield className="w-3.5 h-3.5" aria-hidden="true" />
            Open Finance · BACEN
          </div>

          {/* Title 2 lines — segunda com glow neon */}
          <h2
            className="font-display font-bold text-foreground mt-5 leading-[0.95] tracking-[-0.02em] text-[40px] md:text-[56px] lg:text-[64px]"
            data-testid="conexoes-hero-title"
          >
            <span className="block">SEU BANCO.</span>
            <span
              className="block text-brand-green"
              style={{ textShadow: "0 0 24px rgba(0,255,163,0.45)" }}
            >
              EM TEMPO REAL.
            </span>
          </h2>

          <p className="text-body text-foreground-muted mt-4 max-w-2xl">
            Conecte suas contas via Open Finance e tenha extrato, faturas e
            investimentos sincronizados automaticamente. Sem inputs manuais.
            Zero credenciais armazenadas.
          </p>

          {/* Trust badges */}
          <div
            className="flex flex-wrap items-stretch gap-2.5 md:gap-3 mt-6"
            data-testid="conexoes-hero-trust"
          >
            {TRUST.map(({ icon: Icon, label, sub }) => (
              <div
                key={label}
                className={cn(
                  "flex items-center gap-3 px-4 py-2.5 rounded-xl",
                  "border border-brand-green/30 bg-brand-green/5",
                  "transition-all duration-fast hover:bg-brand-green/10",
                )}
              >
                <Icon className="w-5 h-5 text-brand-green shrink-0" aria-hidden="true" />
                <div className="leading-tight">
                  <div className="text-caption font-bold tracking-wider text-foreground uppercase">{label}</div>
                  <div className="text-[11px] text-foreground-muted">{sub}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Card>
    </motion.div>
  );
}
